// src/components/ErrorBoundary.jsx
import React from 'react';
import {
  forceFreshAppReload,
  isStaleAssetError,
  recoverFromStaleAssetError,
} from '../utils/assetLoadRecovery';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, recoveringAsset: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (!isStaleAssetError(error)) return;

    this.setState({ recoveringAsset: true });

    void recoverFromStaleAssetError(error, {
      source: 'react-error-boundary',
    }).then((reloading) => {
      if (!reloading) {
        this.setState({ recoveringAsset: false });
      }
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.state.recoveringAsset) {
        return (
          <div className="min-h-screen bg-[#f7f7f8] px-4 py-10">
            <div className="mx-auto max-w-xl rounded-3xl border border-gray-200 bg-white p-6 text-center shadow-xl sm:p-8">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-b-[#720e1e]" />
              <h2 className="mt-4 text-lg font-bold text-gray-900">
                Updating UniAir…
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                A newer version is available. We’re refreshing the app automatically.
              </p>
            </div>
          </div>
        );
      }

      const staleAsset = isStaleAssetError(this.state.error);

      return (
        <div className="min-h-screen bg-[#f7f7f8] px-4 py-10">
          <div className="mx-auto max-w-xl rounded-3xl border border-red-200 bg-white p-6 text-center shadow-xl sm:p-8">
            <h2 className="text-lg font-bold text-red-700">
              {staleAsset ? 'UniAir needs to refresh' : 'Something went wrong'}
            </h2>
            <p className="mt-2 text-sm text-red-600">
              {staleAsset
                ? 'Some application files are out of date. Reload UniAir to get the latest version.'
                : this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              type="button"
              onClick={() => forceFreshAppReload('error-boundary-button')}
              className="mt-4 rounded-2xl bg-[#720e1e] px-5 py-3 text-sm font-bold text-white"
            >
              Reload UniAir
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
